import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import { ThemeToggle } from "../components/ThemeToggle.js";

interface Manifest {
  version: string;
  platform: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  publishedAt: string;
  minWindows: string;
  requires: string;
}

export function DownloadPage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/download/manifest.json", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setManifest)
      .catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="bg-radial-deep min-h-screen flex flex-col">
      <header className="px-margin-page py-md flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-primary-fixed-dim">
          <Icon name="sync" className="text-2xl" />
          <span className="text-lg font-black tracking-tighter uppercase">AppsDoCalSync</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link
            to="/login"
            className="text-label-xs font-mono uppercase tracking-widest text-on-surface-variant hover:text-primary-fixed-dim transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-margin-page py-xl">
        <div className="text-center mb-xl">
          <h1 className="text-display-xl text-on-background mb-sm">Windows desktop app</h1>
          <p className="text-body-base text-on-surface-variant max-w-xl mx-auto">
            Install on each PC where you use Outlook. The app reads your local calendar, syncs it to the hub, and
            mirrors changes from your other PCs and connected Gmails — in real time.
          </p>
        </div>

        <div className="glass-card-elevated rounded-xl p-xl flex flex-col md:flex-row items-center gap-lg">
          <div className="w-24 h-24 rounded-2xl bg-primary-container/20 border border-primary-container/40 flex items-center justify-center shrink-0">
            <Icon name="desktop_windows" className="text-primary-fixed-dim" style={{ fontSize: "48px" }} />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-headline-md text-on-surface">AppsDoCalSync for Windows</h2>
            {manifest ? (
              <p className="text-body-sm text-on-surface-variant mt-1">
                v{manifest.version} · {(manifest.sizeBytes / 1024 / 1024).toFixed(0)} MB · {manifest.platform} ·
                published {new Date(manifest.publishedAt).toLocaleDateString()}
              </p>
            ) : error ? (
              <p className="text-body-sm text-error mt-1">Couldn't load manifest: {error}</p>
            ) : (
              <p className="text-body-sm text-on-surface-variant mt-1">Loading manifest…</p>
            )}
          </div>
          <a
            href="/download/AppsDoCalSync.exe"
            className="bg-primary-container text-on-primary-container px-lg py-md rounded-lg text-label-xs font-mono uppercase tracking-widest flex items-center gap-sm shrink-0 active:scale-[0.98] transition-transform"
          >
            <Icon name="download" />
            Download EXE
          </a>
        </div>

        {/* Pre-emptive warning — chrome + smartscreen will both flag the
            unsigned EXE on first run. Setting expectations here keeps people
            from panicking when their browser yells. */}
        <div className="mt-md p-md bg-tertiary-container/15 border border-tertiary-container/30 rounded-lg flex items-start gap-3">
          <Icon name="info" className="text-tertiary-container shrink-0 mt-0.5" />
          <div className="flex-1 text-body-sm text-on-surface">
            <p className="font-semibold text-tertiary-container mb-1">Your browser will warn — that's expected</p>
            <p className="text-on-surface-variant">
              The EXE isn't code-signed yet (cert is on the to-do list), so:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1 text-on-surface-variant">
              <li>
                <span className="text-on-surface">Chrome:</span> "This file might be malware" → click{" "}
                <span className="font-mono text-tertiary-container">Download suspicious file</span> (it's our own
                build, scanned by us before publish).
              </li>
              <li>
                <span className="text-on-surface">SmartScreen on first run:</span> "Windows protected your PC" → click{" "}
                <span className="font-mono text-tertiary-container">More info → Run anyway</span>. Windows remembers
                your decision.
              </li>
              <li>
                Verify the SHA-256 checksum at the bottom of this page if you want to be extra sure.
              </li>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-md mt-lg">
          <Card icon="check_circle" tone="ok" title="What it does">
            <li>Reads your local Outlook calendar (Office 2016 / 2019 / 365 with a configured profile).</li>
            <li>Pushes changes to the AppsDoCalSync hub in real time.</li>
            <li>Receives changes back via WebSocket — events from your other PCs + your connected Gmails appear within seconds.</li>
            <li>Survives restarts, sleeps in the system tray.</li>
          </Card>
          <Card icon="warning" tone="warn" title="Before you install">
            <li>You need a hub account — admin sends you an invitation link.</li>
            <li>Outlook must be installed and have a profile.</li>
            <li>Windows 10 (build 17763) or newer; 64-bit.</li>
            <li>SmartScreen may warn the first run (the EXE isn't code-signed yet) — click "More info → Run anyway".</li>
          </Card>
        </div>

        {manifest && (
          <div className="mt-lg p-md bg-surface-container border border-outline-variant rounded text-body-sm">
            <p className="text-label-xs font-mono text-on-surface-variant uppercase tracking-widest mb-2">
              SHA-256 checksum
            </p>
            <p className="font-mono text-primary-fixed-dim break-all">{manifest.sha256}</p>
            <p className="text-label-xs font-mono text-on-surface-variant uppercase mt-3">
              Verify with PowerShell:{" "}
              <code className="text-primary-fixed-dim">Get-FileHash AppsDoCalSync.exe -Algorithm SHA256</code>
            </p>
          </div>
        )}
      </main>

      <footer className="text-center py-md text-label-xs font-mono text-on-surface-variant uppercase tracking-widest">
        © {new Date().getFullYear()} AppsDoCalSync · AppsDo IT
      </footer>
    </div>
  );
}

function Card({
  icon,
  tone,
  title,
  children,
}: {
  icon: string;
  tone: "ok" | "warn";
  title: string;
  children: React.ReactNode;
}) {
  const colour = tone === "ok" ? "text-primary-fixed-dim" : "text-tertiary-container";
  return (
    <div className="bg-surface-container border border-outline-variant rounded-xl p-md">
      <div className="flex items-center gap-2 mb-sm">
        <Icon name={icon} className={colour} />
        <h3 className="text-headline-md text-on-surface text-base">{title}</h3>
      </div>
      <ul className="space-y-1.5 text-body-sm text-on-surface-variant list-disc pl-5">{children}</ul>
    </div>
  );
}
